/**
 * The team block library (/app/blocks): every saved block with its preview, usage and details.
 * Content is not edited here — a block changes by editing it in a template and saving it again
 * ("Save as block…" → Update an existing block). Rename/describe/tag sends the stored contentJson
 * back unchanged.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Badge, Button, ConfirmModal, Modal, PageHeader } from '../components/ui'
import Toggle from '../components/Toggle'
import PdfPages from '../components/pdf/PdfPages'
import BlockThumbnail from '../components/blocks/BlockThumbnail'
import { loadBlockPreview } from '../components/blocks/blockPreviewCache'
import BlockDetailsFields, {
  fieldStyle, toBlockDetails, validateBlockDetails, type BlockDetailsValue,
} from '../components/blocks/BlockDetailsFields'
import { matchesBlock } from '../blocks/teamBlocks'
import { blocksApiService, type BlockUsage, type ContentBlockSummary } from '../services/blocksApiService'
import { ApiError } from '../services/BaseApiService'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { SecurityRole } from '../constants'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'

const errorText = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback)
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—')

export default function BlocksLibraryPage() {
  const user = useCurrentUser()
  const canEdit = (user?.securityRole ?? 0) >= SecurityRole.EDITOR
  const [blocks, setBlocks] = useState<ContentBlockSummary[] | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [reload, setReload] = useState(0)
  const [previewing, setPreviewing] = useState<ContentBlockSummary | null>(null)
  const [editing, setEditing] = useState<ContentBlockSummary | null>(null)
  const [deleting, setDeleting] = useState<ContentBlockSummary | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    blocksApiService.list({ includeArchived: showArchived })
      .then(b => { if (!cancelled) { setBlocks(b); setError('') } })
      .catch(err => { if (!cancelled) { setBlocks([]); setError(errorText(err, 'Could not load team blocks.')) } })
    return () => { cancelled = true }
  }, [showArchived, reload])

  const categories = useMemo(() => [...new Set((blocks ?? []).map(b => b.category).filter((c): c is string => !!c))].sort(), [blocks])
  const shown = (blocks ?? []).filter(b => matchesBlock(b, query) && (!category || b.category === category) && (showArchived || !b.archived))

  async function setArchived(b: ContentBlockSummary, archived: boolean) {
    setError('')
    try {
      await blocksApiService.archive(b.contentBlockId, archived)
      setNotice(archived ? `Archived “${b.name}”.` : `Restored “${b.name}”.`)
      setReload(r => r + 1)
    } catch (err) {
      setError(errorText(err, 'Updating the block failed.'))
    }
  }

  async function confirmDelete() {
    if (!deleting) return
    setDeleteBusy(true)
    setError('')
    try {
      await blocksApiService.delete(deleting.contentBlockId)
      setNotice(`Deleted “${deleting.name}”.`)
      setDeleting(null)
      setReload(r => r + 1)
    } catch (err) {
      setError(errorText(err, 'Deleting the block failed.'))
    } finally {
      setDeleteBusy(false)
    }
  }

  return (
    <div>
      <PageHeader
        title="Blocks"
        subtitle="Reusable content your team has saved. Insert blocks from the editor's Blocks panel; to change a block's content, edit it in a template and save it as a block again."
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.sm, alignItems: 'center', marginBottom: spacing.lg }}>
        <input type="search" aria-label="Search blocks" placeholder="Search blocks" value={query} onChange={e => setQuery(e.target.value)} style={{ ...fieldStyle, width: 260, maxWidth: '100%' }} />
        <select aria-label="Category" value={category} onChange={e => setCategory(e.target.value)} style={{ ...fieldStyle, width: 180 }}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <Toggle checked={showArchived} onChange={setShowArchived} label="Show archived" />
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}

      {blocks === null ? (
        <div style={{ color: colors.textSecondary }}>Loading…</div>
      ) : shown.length === 0 ? (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.xxl, textAlign: 'center', color: colors.textSecondary, boxShadow: shadow.sm }}>
          {blocks.length === 0 && !showArchived
            ? 'No active team blocks. Archived blocks are hidden — turn on Show archived to see them. To add one, in the template editor select content and choose “Save selection as block”, or use a block’s ⋮⋮ handle → Save as block….'
            : blocks.length === 0
            ? 'No team blocks yet. In the template editor, select content and choose “Save selection as block”, or use a block’s ⋮⋮ handle → Save as block….'
            : 'No blocks match these filters.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: spacing.md }}>
          {shown.map(b => (
            <article key={b.contentBlockId} aria-label={b.name} style={{ display: 'flex', flexDirection: 'column', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.sm, overflow: 'hidden', opacity: b.archived ? 0.75 : 1 }}>
              <button type="button" aria-label={`Preview ${b.name}`} onClick={() => setPreviewing(b)} style={{ padding: 0, border: 'none', borderBottom: `1px solid ${colors.border}`, background: 'none', cursor: 'pointer' }}>
                <BlockThumbnail contentBlockId={b.contentBlockId} revision={b.updatedAt} name={b.name} width={260} />
              </button>
              <div style={{ padding: spacing.md, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: colors.text }}>{b.name}</span>
                  {b.category && <Badge>{b.category}</Badge>}
                  {b.archived && <Badge color={colors.warning} bg={colors.warningLight} border={colors.warningLight}>Archived</Badge>}
                </div>
                {b.description && <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{b.description}</div>}
                {!!b.tags?.length && <div style={{ fontSize: fontSize.xs, color: colors.textMuted }}>{b.tags.map(t => `#${t}`).join(' ')}</div>}
                <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: 'auto' }}>
                  Used {b.usageCount} {b.usageCount === 1 ? 'time' : 'times'} · last used {fmtDate(b.lastUsedAt)}
                  <br />
                  Created by {b.createdByEmail ?? 'unknown'} · {fmtDate(b.createdAt)}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  <Button size="sm" variant="secondary" onClick={() => setPreviewing(b)}>Preview</Button>
                  {canEdit && <Button size="sm" variant="ghost" onClick={() => { setNotice(''); setEditing(b) }}>Edit details</Button>}
                  {canEdit && <Button size="sm" variant="ghost" onClick={() => setArchived(b, !b.archived)}>{b.archived ? 'Restore' : 'Archive'}</Button>}
                  {canEdit && <Button size="sm" variant="ghost" onClick={() => setDeleting(b)} style={{ color: colors.error }}>Delete</Button>}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {previewing && <BlockPreviewModal block={previewing} onClose={() => setPreviewing(null)} />}
      {editing && (
        <EditDetailsModal
          block={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={name => { setEditing(null); setNotice(`Saved “${name}”.`); setReload(r => r + 1) }}
        />
      )}
      <ConfirmModal
        open={!!deleting}
        title="Delete block"
        danger
        confirmLabel="Delete"
        loading={deleteBusy}
        message={<>Delete “{deleting?.name}” from the team library? Templates that already use it keep their own copy; it just can’t be inserted any more. Archive it instead to hide it without deleting.</>}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  )
}

function Banner({ tone, children }: { tone: 'error' | 'success'; children: React.ReactNode }) {
  const err = tone === 'error'
  return (
    <div role={err ? 'alert' : 'status'} style={{ background: err ? colors.errorLight : colors.successLight, color: err ? colors.error : colors.success, border: `1px solid ${err ? colors.errorBorder : colors.successBorder}`, borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md }}>
      {children}
    </div>
  )
}

function BlockPreviewModal({ block, onClose }: { block: ContentBlockSummary; onClose: () => void }) {
  const [blob, setBlob] = useState<Blob | null>(null)
  const [previewError, setPreviewError] = useState('')
  const [usage, setUsage] = useState<BlockUsage[] | null>(null)

  useEffect(() => {
    let cancelled = false
    loadBlockPreview(block.contentBlockId, block.updatedAt)
      .then(b => { if (!cancelled) setBlob(b) })
      .catch(err => { if (!cancelled) setPreviewError(errorText(err, 'Preview unavailable.')) })
    blocksApiService.usage(block.contentBlockId)
      .then(u => { if (!cancelled) setUsage(u) })
      .catch(() => { if (!cancelled) setUsage([]) })
    return () => { cancelled = true }
  }, [block.contentBlockId, block.updatedAt])

  // One row per template, most recent insert first.
  const templates = usage ? [...new Map(usage.map(u => [u.templateId, u])).values()] : null
  const vars = block.variables ?? []

  return (
    <Modal open onClose={onClose} title={block.name} subtitle={block.description ?? undefined} width={860}>
      <div style={{ display: 'flex', gap: spacing.lg, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 460px', minWidth: 0, background: colors.background, padding: spacing.md, borderRadius: radius.sm }}>
          {previewError ? <div style={{ color: colors.error, fontSize: fontSize.sm }}>{previewError}</div>
            : blob ? <PdfPages file={blob} maxWidth={560} />
            : <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Loading preview…</div>}
        </div>
        <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: spacing.md, fontSize: fontSize.sm }}>
          <section aria-label="Fields">
            <h3 style={{ margin: '0 0 6px', fontSize: fontSize.sm, color: colors.text }}>Fields</h3>
            {vars.length === 0 ? <div style={{ color: colors.textMuted }}>No fields.</div> : (
              <ul style={{ margin: 0, paddingLeft: 18, color: colors.textSecondary }}>
                {vars.map(v => <li key={v.name}><code>{v.name}</code> <span style={{ color: colors.textMuted }}>({v.kind})</span></li>)}
              </ul>
            )}
          </section>
          <section aria-label="Where used">
            <h3 style={{ margin: '0 0 6px', fontSize: fontSize.sm, color: colors.text }}>Where used</h3>
            {templates === null ? <div style={{ color: colors.textMuted }}>Loading…</div>
              : templates.length === 0 ? <div style={{ color: colors.textMuted }}>Not inserted into any template yet.</div>
              : (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {templates.map(u => (
                    <li key={u.templateId} style={{ marginBottom: 4 }}>
                      <Link to={`/app/templates/${u.templateId}`} onClick={onClose} style={{ color: colors.primary }}>{u.templateName ?? 'Untitled template'}</Link>
                      <div style={{ fontSize: fontSize.xs, color: colors.textMuted }}>{fmtDate(u.insertedAt)}{u.insertedByEmail ? ` · ${u.insertedByEmail}` : ''}</div>
                    </li>
                  ))}
                </ul>
              )}
          </section>
        </div>
      </div>
    </Modal>
  )
}

function EditDetailsModal({ block, categories, onClose, onSaved }: {
  block: ContentBlockSummary
  categories: string[]
  onClose: () => void
  onSaved: (name: string) => void
}) {
  const [details, setDetails] = useState<BlockDetailsValue>({
    name: block.name, description: block.description ?? '', category: block.category ?? '', tags: block.tags ?? [],
  })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  async function save() {
    const invalid = validateBlockDetails(details)
    if (invalid) { setError(invalid); return }
    setSaving(true)
    setError('')
    try {
      const { contentJson } = await blocksApiService.getBlock(block.contentBlockId)
      const input = toBlockDetails(details)
      await blocksApiService.update(block.contentBlockId, { ...input, contentJson })
      onSaved(input.name)
    } catch (err) {
      setError(errorText(err, 'Saving the block failed.'))
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit block details"
      width={460}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving}>Save</Button>
        </>
      }
    >
      <form onSubmit={e => { e.preventDefault(); save() }} style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <BlockDetailsFields value={details} onChange={setDetails} categories={categories} autoFocus />
        {error && <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm }}>{error}</div>}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}
